import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { bigKind, lookPoint, looksRng, SkyLook, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The swarm: murmurations of black specks wheel over the board, folding and stretching like a flock of
 * starlings, every nightmare comes with a cloud of flies buzzing about it, and a fallen one scatters its
 * cloud. Under a vault (crypts, the Furrows) only the flies: a flock wheeling over the camera's point
 * there reads as moths stuck to the hero.
 */

const FLOCK_VERT = /* glsl */ `
attribute vec4 aSpeck; // u, v, phase, flock
uniform float uTime;
uniform float uHigh;
uniform float uSpan;
uniform vec3 uLook;
varying float vShade;
varying vec2 vUv;
void main() {
  vUv = uv * 2.0 - 1.0;
  float t = uTime;
  float f = aSpeck.w;
  vec3 c = uLook + vec3(sin(t * 0.13 + f * 4.0) * uSpan, uHigh + sin(t * 0.31 + f) * 1.2, cos(t * 0.11 + f * 7.0) * uSpan * 0.6);
  // A breathing, turning cloud: spread over an ellipsoid whose axes swell and shrink out of step.
  float u = aSpeck.x * 6.2832 + t * (0.35 + aSpeck.z * 0.25);
  float v = (aSpeck.y - 0.5) * 3.1416 + sin(t * 0.7 + aSpeck.x * 6.0) * 0.4;
  vec3 ax = vec3(2.2 + 1.0 * sin(t * 0.43 + f), 0.7 + 0.4 * sin(t * 0.37 + f * 2.0), 1.1 + 0.7 * cos(t * 0.29 + f * 3.0));
  // Denser toward the heart of the cloud.
  ax *= 0.35 + 0.65 * sqrt(aSpeck.z);
  vec3 q = vec3(cos(u) * cos(v), sin(v), sin(u) * cos(v)) * ax;
  float r = t * 0.17 + f * 2.0 + sin(t * 0.21) * 0.8;
  q.xz = mat2(cos(r), -sin(r), sin(r), cos(r)) * q.xz;
  // A ripple running through the flock.
  q.y += sin(q.x * 0.9 - t * 2.3) * 0.35;
  vec3 p = c + q;
  vec4 mv = viewMatrix * vec4(p, 1.0);
  float flap = abs(sin(floor(t * 12.0) * 0.9 + aSpeck.z * 30.0));
  mv.xy += position.xy * vec2(0.07 * (0.35 + flap), 0.03);
  vShade = 0.6 + 0.4 * flap;
  gl_Position = projectionMatrix * mv;
}`;

const FLOCK_FRAG = /* glsl */ `
uniform vec3 uColor;
varying float vShade;
varying vec2 vUv;
void main() {
  float a = 1.0 - smoothstep(0.5, 1.0, length(vUv));
  if (a < 0.05) discard;
  gl_FragColor = vec4(uColor * vShade, 0.85 * a);
  #include <colorspace_fragment>
}`;

const FLY = new THREE.Color('#121016');

export const buildSwarm = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-swarm';
  const low = ctx.low;
  const rnd = looksRng(0x5a2a);
  const sky = new SkyLook(ctx, { hemi: ['#a8a294', '#2a2620', 0.85], key: ['#e8d8c0', 0.85], background: '#4a4a44', mist: { low: '#5a5648', mid: '#423f36', far: '#2a2924', alpha: 1.1 } });
  const flocks = 2;
  const count = (low ? 220 : 700) * flocks;
  const geo = new THREE.InstancedBufferGeometry();
  const quad = new THREE.PlaneGeometry(1, 1);
  geo.index = quad.index;
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  const seeds = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) seeds.set([rnd(), rnd(), rnd(), i % flocks], i * 4);
  geo.setAttribute('aSpeck', new THREE.InstancedBufferAttribute(seeds, 4));
  geo.instanceCount = count;
  const mat = new THREE.ShaderMaterial({
    vertexShader: FLOCK_VERT,
    fragmentShader: FLOCK_FRAG,
    uniforms: { uLook: { value: new THREE.Vector3() }, uTime: { value: 0 }, uHigh: { value: 7 }, uSpan: { value: 4.5 }, uColor: { value: new THREE.Color('#141218') } },
    transparent: true,
    depthWrite: false,
  });
  const flock = new THREE.Mesh(geo, mat);
  flock.frustumCulled = false;
  flock.renderOrder = 12;
  // Flies: dark specks drawn over what they circle.
  const flies = new GlowLayer(low ? 400 : 1200, THREE.NormalBlending);
  flies.mesh.renderOrder = 12;
  group.add(flies.mesh);
  if (!ctx.crypt) group.add(flock);
  let enemies: { id: number; kind: string }[] = [];
  const look = new THREE.Vector3();

  return {
    group,
    tick: (s: BattleState) => {
      enemies = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall').map((e) => ({ id: e.id, kind: e.kind }));
    },
    event: (_s, e) => {
      if (e.t !== 'die') return false;
      // Its cloud scatters.
      ctx.vfx.burst(ctx.at(e.x, e.y, 0.5), { n: low ? 6 : 14, color: '#141218', speed: 0.12, up: 0.08, size: 0.05, life: 12, gravity: -0.004, smoke: true, spread: 0.3 });
      return false;
    },
    frame: (t, camera) => {
      (mat.uniforms.uTime as { value: number }).value = t;
      // The flocks wheel over wherever the camera looks, slowly (so they drift across, not stick to it).
      lookPoint(camera, look);
      (mat.uniforms.uLook as { value: THREE.Vector3 }).value.lerp(look, 0.01);
      sky.pulse({});
      flies.begin();
      const per = low ? 4 : 8;
      for (const e of enemies) {
        const p = ctx.pos(e.id);
        if (!p) continue;
        const big = bigKind(e.kind);
        const n = big ? per * 2 : per;
        const rr = big ? 0.9 : 0.4;
        for (let k = 0; k < n; k++) {
          const ph = e.id * 1.7 + k * 2.39;
          const a = t * (4 + (k % 3)) + ph;
          // Jittery orbits, stop-motion like the rest.
          const tf = Math.floor(t * 24) / 24;
          const r = rr * (0.6 + 0.4 * Math.sin(tf * 5 + ph));
          flies.add(p.x + Math.cos(a) * r, p.y + 0.3 + rr * 0.6 + Math.sin(tf * 7 + ph) * 0.18, p.z + Math.sin(a * 1.1) * r, FLY, 0.045, 0.85);
        }
      }
      flies.end();
    },
    dispose: () => {
      sky.restore();
      geo.dispose();
      quad.dispose();
      mat.dispose();
      flies.mesh.geometry.dispose();
      flies.material.dispose();
    },
  };
};
